import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import { getSales, type GetSalesResponse, type PeriodUnit } from '@apis/summary';
import {
  getTimestampRangeRollingDaysKst,
  getTimestampRangeToDateKst,
} from '@utils/time';

export type SalesPreset = '7D' | '30D' | '90D' | '12M' | 'WTD' | 'MTD' | 'YTD';

type TimeRange = { from: number; to: number };

function getSalesRangeKst(preset: SalesPreset): TimeRange {
  switch (preset) {
    case '7D':
      return getTimestampRangeRollingDaysKst(7);
    case '30D':
      return getTimestampRangeRollingDaysKst(30);
    case '90D':
      return getTimestampRangeRollingDaysKst(90);
    case '12M':
      /**
       * "12 months" is approximated as a rolling 365-day window.
       * The chart is displayed as monthly buckets, which matches the expected UX.
       */
      return getTimestampRangeRollingDaysKst(365);
    case 'WTD':
      return getTimestampRangeToDateKst('WEEKS');
    case 'MTD':
      return getTimestampRangeToDateKst('MONTHS');
    case 'YTD':
      return getTimestampRangeToDateKst('YEARS');
  }
}

function getBucketUnit(preset: SalesPreset): PeriodUnit {
  /**
   * Bucketing policy:
   * - 7D / 30D / WTD / MTD: daily buckets
   * - 90D: weekly buckets
   * - 12M / YTD: monthly buckets
   */
  if (preset === '90D') return 'WEEKS';
  if (preset === '12M' || preset === 'YTD') return 'MONTHS';
  return 'DAYS';
}

export const useSummarySalesByPeriod = (preset: SalesPreset) => {
  const range = getSalesRangeKst(preset);
  const periodUnit = getBucketUnit(preset);

  const queryOptions: UseQueryOptions<PageResponse<GetSalesResponse>, Error> = {
    queryKey: [
      'dashboardSummarySalesByPeriod',
      preset,
      periodUnit,
      range.from,
      range.to,
    ],
    queryFn: () =>
      getSales({
        periodUnit,
        from: range.from,
        to: range.to,
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  };

  const query = useQuery(queryOptions);

  return {
    ...query,
    preset,
    range,
    periodUnit,
  };
};
